import {
  RouteLocationRaw,
  RouteRecordRaw,
  createRouter,
  createWebHistory,
} from 'vue-router';

export const RouteTypes = {
  DOCTOR: 'DOCTOR',
  PATIENT: 'PATIENT',
  ADMIN: 'ADMIN',
} as const;

type RouteType = (typeof RouteTypes)[keyof typeof RouteTypes];

export type RouteMeta = {
  type: RouteType;
  requiredAuth: boolean;
  redirectTo: RouteLocationRaw;
};

type RouteRecordRawWithMeta = RouteRecordRaw & {
  children?: (RouteRecordRaw & {
    meta?: RouteMeta;
  })[];
};

const UnauthorizedRouteForDoctor: RouteMeta = {
  type: RouteTypes.DOCTOR,
  requiredAuth: false,
  redirectTo: { name: 'doctorsTop' },
};

const AuthorizedRouteForDoctor: RouteMeta = {
  type: RouteTypes.DOCTOR,
  requiredAuth: true,
  redirectTo: { name: 'doctorsLogin' },
};

const UnauthorizedRouteForPatient: RouteMeta = {
  type: RouteTypes.PATIENT,
  requiredAuth: false,
  redirectTo: { name: 'patientsTop' },
};

const UnauthorizedRouteForPatientNotRedirectTo: RouteMeta = {
  type: RouteTypes.PATIENT,
  requiredAuth: false,
  // redirectTo: { name: 'patientsTop' },
};

const AuthorizedRouteForPatient: RouteMeta = {
  type: RouteTypes.PATIENT,
  requiredAuth: true,
  redirectTo: { name: 'patientsLogin' },
};

const UnauthorizedRouteForAdmin: RouteMeta = {
  type: RouteTypes.ADMIN,
  requiredAuth: false,
  redirectTo: { name: 'adminsApproveDoctors' },
};

const AuthorizedRouteForAdmin: RouteMeta = {
  type: RouteTypes.ADMIN,
  requiredAuth: true,
  redirectTo: { name: 'adminsLogin' },
};

const routes: Readonly<RouteRecordRawWithMeta[]> = [
  {
    path: '/terms-of-service',
    components: {
      default: () => import('@/components/pages/TermsOfService.vue'),
      Footer: () => import('@/components/layouts/Footer.vue'),
    },
  },
  {
    path: '/terms-of-service-doctors',
    components: {
      default: () => import('@/components/pages/TermsOfServiceDoctor.vue'),
      Footer: () => import('@/components/layouts/Footer.vue'),
    },
  },
  {
    path: '/privacy-policy',
    components: {
      default: () => import('@/components/pages/PrivacyPolicy.vue'),
      Footer: () => import('@/components/layouts/Footer.vue'),
    },
  },
  {
    path: '/',
    name: 'main',
    components: {
      default: () => import('@/components/pages/Doctors/Top/Container.vue'),
      Header: () => import('@/components/layouts/Header/Doctor.vue'),
      Footer: () => import('@/components/layouts/Footer.vue'),
    },
    meta: AuthorizedRouteForDoctor,
  },
  {
    path: '/:catchAll(.*)',
    component: () => import('@/components/pages/NotFound.vue'),
  },
  {
    path: '/doctors/',
    children: [
      {
        path: 'login',
        name: 'doctorsLogin',
        components: {
          default: () =>
            import('@/components/pages/Doctors/Auth/LoginPage.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForDoctor,
      },
      {
        path: 'registration',
        name: 'doctorsRegistration',
        components: {
          default: () =>
            import(
              '@/components/pages/Doctors/SignUp/Registration/Container.vue'
            ),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForDoctor,
      },
      {
        path: 'registration/complete',
        name: 'doctorsRegistrationComplete',
        components: {
          default: () =>
            import(
              '@/components/pages/Doctors/SignUp/Registration/Complete.vue'
            ),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForDoctor,
      },
      {
        path: 'sign-up',
        name: 'doctorsSignUp',
        components: {
          default: () =>
            import('@/components/pages/Doctors/SignUp/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForDoctor,
      },
      {
        path: 'sign-up/complete',
        name: 'doctorsSignUpComplete',
        components: {
          default: () =>
            import('@/components/pages/Doctors/SignUp/Complete.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForDoctor,
      },
      {
        path: 'reset-password',
        name: 'doctorsResetPassword',
        components: {
          default: () =>
            import('@/components/pages/Doctors/ResetPassword/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForDoctor,
      },
      {
        path: 'reset-password/sent-email',
        components: {
          default: () =>
            import('@/components/pages/Doctors/ResetPassword/SentEmail.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForDoctor,
      },
      {
        path: 'reset-password/new-password',
        components: {
          default: () =>
            import(
              '@/components/pages/Doctors/ResetPassword/NewPassword/Container.vue'
            ),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForDoctor,
      },
      {
        path: 'reset-password/complete',
        components: {
          default: () =>
            import('@/components/pages/Doctors/ResetPassword/Complete.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForDoctor,
      },
      {
        path: 'top',
        name: 'doctorsTop',
        components: {
          default: () => import('@/components/pages/Doctors/Top/Container.vue'),
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'patients',
        name: 'doctorsPatients',
        components: {
          default: () =>
            import('@/components/pages/Doctors/Patients/Container.vue'),
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'patients/:patientId',
        name: 'doctorsPatientDetail',
        components: {
          default: () =>
            import('@/components/pages/Patients/Detail/Container.vue'),
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'patients/:patientId/order-histories',
        name: 'doctorsPatientOrderHistories',
        components: {
          default: () =>
            import('@/components/pages/Common/Patients/Detail/Container.vue'),
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'patients/:patientId/recipes',
        name: 'doctorsPatientRecipeDetail',
        components: {
          default: () =>
            import('@/components/pages/Patients/RecipeDetail/Container.vue'),
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        props: {
          default: (route: { query: { id: string; label: string } }) => ({
            id: route.query.id,
            label: route.query.label,
          }),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'patients/:patientId/questionnaire',
        name: 'doctorsPatientQuestionnaire',
        components: {
          default: () =>
            import('@/components/pages/Patients/Questionnaire/Container.vue'),
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'patients/:patientId/questionnaire/:answerId',
        name: 'doctorsPatientQuestionnaireEdit',
        components: {
          default: () =>
            import('@/components/pages/Patients/Questionnaire/Container.vue'),
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'recipes',
        name: 'doctorsRecipes',
        components: {
          default: () =>
            import('@/components/pages/Doctors/Recipes/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'recipes/registration',
        name: 'doctorsRecipeRegistration',
        components: {
          default: () =>
            import(
              '@/components/pages/Doctors/RecipeRegistration/Container.vue'
            ),
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        props: {
          default: (route: {
            query: { recipe: string; catalogId: string; mode: string };
          }) => ({
            recipe: route.query.recipe || null,
            catalogId: route.query.catalogId || null,
            mode: route.query.mode || null,
          }),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'recipe-histories',
        name: 'doctorsHistories',
        components: {
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          default: () =>
            import('@/components/pages/Doctors/RecipeHistories/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'catalogs',
        name: 'doctorsCatalogs',
        components: {
          default: () =>
            import('@/components/pages/Doctors/Catalog/Container.vue'),
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'catalog',
        name: 'doctorsCatalogsDetail',
        components: {
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          default: () =>
            import('@/components/pages/Doctors/CatalogDetail/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        props: {
          default: (route: { query: { catalogId: string } }) => ({
            catalogId: route.query.catalogId || null,
          }),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'my-recipe',
        name: 'doctorsMyRecipes',
        components: {
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          default: () =>
            import('@/components/pages/Doctors/MyRecipe/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'my-recipe/:recipeId',
        name: 'doctorsMyRecipeDetail',
        components: {
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          default: () =>
            import('@/components/pages/Doctors/MyRecipeDetail/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
      },
      {
        path: 'my-recipe/product/',
        name: 'doctorsMyRecipeProduct',
        components: {
          Header: () => import('@/components/layouts/Header/Doctor.vue'),
          default: () =>
            import('@/components/pages/Doctors/MyRecipeProduct/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForDoctor,
        props: {
          default: (route: {
            query: { recipeId: string; shopify_product_id: string };
          }) => ({
            recipeId: route.query.recipeId,
            catalogId: route.query.shopify_product_id,
          }),
        },
      },
    ],
  },
  {
    path: '/user/',
    children: [
      {
        path: 'login',
        name: 'patientsLogin',
        components: {
          default: () =>
            import('@/components/pages/Patients/LogIn/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatient,
      },
      {
        path: 'register',
        components: {
          default: () =>
            import('@/components/pages/Patients/Register/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatientNotRedirectTo,
      },
      {
        path: 'register/login',
        name: 'patientRegisterLogin',
        components: {
          default: () =>
            import('@/components/pages/Patients/Register/LogIn/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatientNotRedirectTo,
      },
      {
        path: 'register/complete',
        name: 'patientRegisterComplete',
        components: {
          default: () =>
            import(
              '@/components/pages/Patients/Register/Complete/Container.vue'
            ),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatientNotRedirectTo,
      },
      {
        path: 'sign-up',
        components: {
          default: () =>
            import('@/components/pages/Patients/SignUp/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatientNotRedirectTo,
      },
      {
        path: 'sign-up/complete',
        components: {
          default: () =>
            import('@/components/pages/Patients/SignUp/Complete/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatientNotRedirectTo,
      },
      {
        path: 'reset-password',
        name: 'patientsResetPassword',
        components: {
          default: () =>
            import('@/components/pages/Patients/ResetPassword/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatient,
      },
      {
        path: 'reset-password/sent-email',
        components: {
          default: () =>
            import('@/components/pages/Patients/ResetPassword/SentEmail.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatient,
      },
      {
        path: 'reset-password/new-password',
        components: {
          default: () =>
            import(
              '@/components/pages/Patients/ResetPassword/NewPassword/Container.vue'
            ),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatient,
      },
      {
        path: 'reset-password/complete',
        components: {
          default: () =>
            import('@/components/pages/Patients/ResetPassword/Complete.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForPatient,
      },
      {
        path: 'catalogs',
        name: 'patientsCatalogs',
        components: {
          Header: () => import('@/components/layouts/Header/Patient.vue'),
          default: () =>
            import('@/components/pages/Patients/Catalogs/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForPatient,
      },
      {
        path: 'cart',
        components: {
          Header: () => import('@/components/layouts/Header/Patient.vue'),
          default: () =>
            import('@/components/pages/Patients/Cart/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForPatient,
      },
      {
        path: 'top',
        name: 'patientsTop',
        components: {
          Header: () => import('@/components/layouts/Header/Patient.vue'),
          default: () =>
            import('@/components/pages/Patients/Detail/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForPatient,
      },
      {
        path: 'order-histories',
        name: 'patientsOrderHistories',
        components: {
          Header: () => import('@/components/layouts/Header/Patient.vue'),
          default: () =>
            import('@/components/pages/Common/Patients/Detail/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForPatient,
      },
      {
        path: 'catalogs/:id',
        components: {
          Header: () => import('@/components/layouts/Header/Patient.vue'),
          default: () =>
            import('@/components/pages/Patients/CatalogDetail/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForPatient,
      },
      {
        path: 'recipes',
        components: {
          Header: () => import('@/components/layouts/Header/Patient.vue'),
          default: () =>
            import('@/components/pages/Patients/RecipeDetail/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        props: {
          default: (route: { query: { id: string; label: string } }) => ({
            id: route.query.id,
            label: route.query.label,
          }),
        },
        meta: AuthorizedRouteForPatient,
      },
      {
        path: 'news',
        name: 'patientsNews',
        components: {
          Header: () => import('@/components/layouts/Header/Patient.vue'),
          default: () =>
            import('@/components/pages/Patients/News/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForPatient,
      },
    ],
  },
  {
    path: '/admins/',
    children: [
      {
        path: 'login',
        name: 'adminsLogin',
        components: {
          default: () =>
            import('@/components/pages/Admins/LogIn/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForAdmin,
      },
      // {
      //   path: 'registration',
      //   name: 'adminsRegistration',
      //   components: {
      //     default: () =>
      //       import(
      //         '@/components/pages/Admins/SignUp/Registration/Container.vue'
      //       ),
      //     Footer: () => import('@/components/layouts/Footer.vue'),
      //   },
      //   meta: UnauthorizedRouteForAdmin,
      // },
      {
        path: 'registration/complete',
        name: 'adminsRegistrationComplete',
        components: {
          default: () =>
            import(
              '@/components/pages/Admins/SignUp/Registration/Complete.vue'
            ),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForAdmin,
      },
      {
        path: 'sign-up',
        name: 'adminsSignUp',
        components: {
          default: () =>
            import('@/components/pages/Admins/SignUp/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForAdmin,
      },
      {
        path: 'sign-up/complete',
        name: 'adminsSignUpComplete',
        components: {
          default: () =>
            import('@/components/pages/Admins/SignUp/Complete.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForAdmin,
      },
      {
        path: 'reset-password',
        name: 'adminsResetPassword',
        components: {
          default: () =>
            import('@/components/pages/Admins/ResetPassword/Container.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForAdmin,
      },
      {
        path: 'reset-password/sent-email',
        name: 'adminsResetPasswordSentEmail',
        components: {
          default: () =>
            import('@/components/pages/Admins/ResetPassword/SentEmail.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForAdmin,
      },
      {
        path: 'reset-password/new-password',
        name: 'adminsResetPasswordNewPassword',
        components: {
          default: () =>
            import(
              '@/components/pages/Admins/ResetPassword/NewPassword/Container.vue'
            ),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForAdmin,
      },
      {
        path: 'reset-password/complete',
        name: 'adminsResetPasswordComplete',
        components: {
          default: () =>
            import('@/components/pages/Admins/ResetPassword/Complete.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: UnauthorizedRouteForAdmin,
      },
      {
        path: 'approve-doctor',
        name: 'adminsApproveDoctors',
        components: {
          default: () =>
            import('@/components/pages/Admins/Doctors/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'registered-doctors',
        components: {
          default: () =>
            import('@/components/pages/Admins/RegisteredDoctors/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'doctors',
        name: 'doctors',
        components: {
          default: () =>
            import('@/components/pages/Admins/Doctors/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'doctors/:doctorId',
        name: 'doctorDetail',
        components: {
          default: () =>
            import('@/components/pages/Admins/DoctorDetail/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'recipes',
        name: 'adminRecipes',
        components: {
          default: () =>
            import('@/components/pages/Admins/RecipeList/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'recipes/registration',
        name: 'adminsRecipeRegistration',
        components: {
          default: () =>
            import(
              '@/components/pages/Doctors/RecipeRegistration/Container.vue'
            ),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        props: {
          default: (route: {
            query: { recipe: string; catalogId: string; mode: string };
          }) => ({
            recipe: route.query.recipe || null,
            catalogId: route.query.catalogId || null,
            mode: route.query.mode || null,
          }),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'recipes/templates',
        name: 'adminRecipeTemplate',
        components: {
          default: () =>
            import('@/components/pages/Doctors/Recipes/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'recipes/templates/:recipeId',
        name: 'adminRecipeTemplateDetail',
        components: {
          default: () =>
            import('@/components/pages/Doctors/Recipes/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'recipes/:recipeId',
        name: 'adminRecipeDetail',
        components: {
          default: () =>
            import('@/components/pages/Admins/RecipeDetail/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'patients',
        name: 'adminsPatients',
        components: {
          default: () =>
            import('@/components/pages/Doctors/Patients/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'patients/:patientId',
        name: 'adminsPatientDetail',
        components: {
          default: () =>
            import('@/components/pages/Common/Patients/Detail/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'orders',
        name: 'adminsOrders',
        components: {
          default: () =>
            import('@/components/pages/Admins/Orders/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'orders/detail/:orderId',
        name: 'adminOrdersDetail',
        components: {
          default: () =>
            import('@/components/pages/Admins/Orders/Detail/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'settlements',
        name: 'adminSettlements',
        components: {
          default: () =>
            import('@/components/pages/Admins/Settlements/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'settlements/detail/:settlementId',
        name: 'adminSettlementsDetail',
        components: {
          default: () =>
            import(
              '@/components/pages/Admins/Settlements/Detail/Container.vue'
            ),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'users',
        name: 'adminUsers',
        components: {
          default: () =>
            import('@/components/pages/Admins/AdminUsers/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'logistics',
        name: 'adminLogistics',
        components: {
          default: () =>
            import('@/components/pages/Admins/Logistics/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'news',
        name: 'adminsNews',
        components: {
          default: () => import('@/components/pages/Admins/News/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
      {
        path: 'news/:newsId',
        name: 'adminsNewsDetail',
        components: {
          default: () =>
            import('@/components/pages/Admins/NewsDetail/Container.vue'),
          Header: () => import('@/components/layouts/Header/Admin.vue'),
          Footer: () => import('@/components/layouts/Footer.vue'),
        },
        meta: AuthorizedRouteForAdmin,
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // ブラウザの戻る・進むボタンで遷移した場合、保存されたスクロール位置に戻る
    if (savedPosition) {
      // コンテンツが非同期で読み込まれる場合に備えて遅延実行
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(savedPosition);
        }, 300);
      });
    }
    // それ以外は先頭にスクロール
    return { top: 0 };
  },
});

/**
 * 各ユーザーのログインルート
 */
const LoginRoute = {
  DOCTOR: '/doctors/login',
  USER: '/user/login',
  ADMIN: '/admins/login',
} as const;

/**
 * バックエンドのガード名
 */
const Guard = {
  DOCTOR: 'doctor',
  USER: 'patient',
  ADMIN: 'admin',
} as const;

/**
 * 対象のパスがどのユーザーに該当するかを判定する
 */
export const isDoctorPath = (path: string) =>
  path.startsWith('/doctors') || path === '/';
export const isUserPath = (path: string) => path.startsWith('/user');
export const isAdminPath = (path: string) => path.startsWith('/admins');

/**
 * 認証エラー時に使用：フロントのパスからログインページへの遷移先に変換する
 */
export const convertPathToLoginRoute = (path: string) => {
  if (isDoctorPath(path)) {
    return LoginRoute.DOCTOR;
  }

  if (isUserPath(path)) {
    return LoginRoute.USER;
  }

  if (isAdminPath(path)) {
    return LoginRoute.ADMIN;
  }

  return '/';
};

/**
 * 未承認エラー時に使用：フロントのパスからバックエンドのガード名に変換する
 */
export const convertPathToGuardName = (path: string) => {
  if (isDoctorPath(path)) {
    return Guard.DOCTOR;
  }

  if (isUserPath(path)) {
    return Guard.USER;
  }

  if (isAdminPath(path)) {
    return Guard.ADMIN;
  }

  return '';
};

export default router;
